import { useEffect, useMemo, useState } from 'react';
import { api, UnauthorizedError, type Row } from '../api';
import { fmt } from '../lib/format';
import {
  inRange,
  periodRanges,
  unionRange,
  type Mode,
  type Period,
  type Range,
} from '../lib/periods';

type Props = {
  accountId: string;
  period: Period;
  mode: Mode;
  onPeriodChange: (p: Period) => void;
  onModeChange: (m: Mode) => void;
  onSessionExpired: () => void;
};

type Totals = {
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  conversion_value: number;
  ctr: number;
  cpc: number;
  cpm: number;
  cpa: number;
  roas: number;
};

type MetricDef = {
  key: keyof Totals;
  label: string;
  fmt: (n: number) => string;
  /** When true, "down" is good (e.g., CPC, CPA). */
  lowerIsBetter?: boolean;
};

const METRICS: MetricDef[] = [
  { key: 'spend', label: 'Gasto', fmt: fmt.brl },
  { key: 'impressions', label: 'Impressões', fmt: fmt.num },
  { key: 'clicks', label: 'Cliques', fmt: fmt.num },
  { key: 'conversions', label: 'Conversões', fmt: fmt.num },
  { key: 'ctr', label: 'CTR', fmt: fmt.pct },
  { key: 'cpc', label: 'CPC', fmt: fmt.brl, lowerIsBetter: true },
  { key: 'cpm', label: 'CPM', fmt: fmt.brl, lowerIsBetter: true },
  { key: 'cpa', label: 'CPA', fmt: fmt.brl, lowerIsBetter: true },
  { key: 'roas', label: 'ROAS', fmt: (n) => (n ? `${n.toFixed(2)}×` : '—') },
];

function sumTotals(rows: Row[], range: Range): Totals {
  let spend = 0;
  let impressions = 0;
  let clicks = 0;
  let conversions = 0;
  let conversion_value = 0;
  for (const r of rows) {
    if (!inRange(r.date_start, range)) continue;
    spend += r.spend || 0;
    impressions += r.impressions || 0;
    clicks += r.clicks || 0;
    conversions += r.conversions || 0;
    conversion_value += r.conversion_value || 0;
  }
  const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
  const cpc = clicks > 0 ? spend / clicks : 0;
  const cpm = impressions > 0 ? (spend / impressions) * 1000 : 0;
  const cpa = conversions > 0 ? spend / conversions : 0;
  const roas = spend > 0 ? conversion_value / spend : 0;
  return { spend, impressions, clicks, conversions, conversion_value, ctr, cpc, cpm, cpa, roas };
}

function delta(curr: number, prev: number): { pct: number; dir: 'up' | 'down' | 'flat' } {
  if (!prev) {
    if (!curr) return { pct: 0, dir: 'flat' };
    return { pct: Infinity, dir: 'up' };
  }
  const pct = ((curr - prev) / prev) * 100;
  if (Math.abs(pct) < 0.05) return { pct: 0, dir: 'flat' };
  return { pct, dir: pct > 0 ? 'up' : 'down' };
}

function deltaTone(dir: 'up' | 'down' | 'flat', lowerIsBetter?: boolean): 'good' | 'bad' | 'flat' {
  if (dir === 'flat') return 'flat';
  const good = lowerIsBetter ? dir === 'down' : dir === 'up';
  return good ? 'good' : 'bad';
}

function deltaLabel(pct: number, dir: 'up' | 'down' | 'flat'): string {
  if (dir === 'flat') return '±0%';
  if (!isFinite(pct)) return dir === 'up' ? '↑ novo' : '↓ —';
  const arrow = dir === 'up' ? '↑' : '↓';
  return `${arrow} ${Math.abs(pct).toFixed(1)}%`;
}

export function QuickView({
  accountId,
  period,
  mode,
  onPeriodChange,
  onModeChange,
  onSessionExpired,
}: Props) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ranges = useMemo(() => periodRanges(period, mode), [period, mode]);

  useEffect(() => {
    if (!accountId) return;
    const span = unionRange(ranges.current, ranges.previous);
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .insights({ account_id: accountId, level: 'campaign', since: span.since, until: span.until })
      .then((r) => {
        if (cancelled) return;
        setRows(r.data);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof UnauthorizedError) {
          onSessionExpired();
          return;
        }
        setError(String(e.message || e));
        setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, ranges.current.since, ranges.current.until, ranges.previous.since, ranges.previous.until, onSessionExpired]);

  const current = useMemo(() => sumTotals(rows, ranges.current), [rows, ranges.current]);
  const previous = useMemo(() => sumTotals(rows, ranges.previous), [rows, ranges.previous]);

  return (
    <section className="quickview" data-testid="quickview">
      <div className="quickview-head">
        <div className="quickview-title">Visão rápida</div>

        <div className="seg" role="tablist" aria-label="Período" data-testid="qv-period">
          {(['day', 'week', 'month'] as Period[]).map((p) => (
            <button
              key={p}
              role="tab"
              aria-selected={period === p}
              className={`seg-btn ${period === p ? 'active' : ''}`}
              onClick={() => onPeriodChange(p)}
              data-testid={`qv-period-${p}`}
            >
              {p === 'day' ? 'Dia' : p === 'week' ? 'Semana' : 'Mês'}
            </button>
          ))}
        </div>

        <div className="seg" role="tablist" aria-label="Modo de comparação" data-testid="qv-mode">
          {(['rolling', 'todate'] as Mode[]).map((m) => (
            <button
              key={m}
              role="tab"
              aria-selected={mode === m}
              className={`seg-btn ${mode === m ? 'active' : ''}`}
              onClick={() => onModeChange(m)}
              data-testid={`qv-mode-${m}`}
            >
              {m === 'rolling' ? 'Rolling' : 'Até hoje'}
            </button>
          ))}
        </div>

        <div className="quickview-range">
          <span className="rng-cur" title={`${ranges.current.since} → ${ranges.current.until}`}>
            {ranges.currentLabel}
          </span>
          <span className="rng-sep">vs</span>
          <span className="rng-prev" title={`${ranges.previous.since} → ${ranges.previous.until}`}>
            {ranges.previousLabel}
          </span>
        </div>
      </div>

      {error && <div className="error" data-testid="qv-error">{error}</div>}

      <div className="kpi-grid" data-testid="qv-kpis">
        {METRICS.map((m) => {
          const cur = current[m.key];
          const prev = previous[m.key];
          const d = delta(cur, prev);
          const tone = deltaTone(d.dir, m.lowerIsBetter);
          return (
            <div key={String(m.key)} className="kpi" data-testid={`qv-kpi-${String(m.key)}`}>
              <div className="kpi-label">{m.label}</div>
              <div className="kpi-value">{loading ? '—' : m.fmt(cur)}</div>
              <div className="kpi-foot">
                <span className={`kpi-delta tone-${tone}`}>{loading ? ' ' : deltaLabel(d.pct, d.dir)}</span>
                <span className="kpi-prev">ant. {m.fmt(prev)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
